
/**
 * Module dependencies.
 */

import { assetUrlResolve } from 'src/core/utils/url-resolver';
import { color, media, units } from 'src/styles/utils';
import { navElementAttrs } from 'src/components/navbar/nav-element';
import { prop, theme } from 'styled-tools';
import { useTranslate } from 'src/core/utils/translator';
import React, { ReactElement } from 'react';
import map from 'lodash/map';
import styled from 'styled-components';

/**
 * `Logo` type.
 */

type Logo = {
  height: number,
  href?: string,
  name: string,
  src: string,
  width: number
};

/**
 * Mobile scale.
 *
 * The design keeps the same logo cluster on mobile, scaled down. Deriving the
 * small sizes from the design ones avoids maintaining two size tables.
 */

const mobileScale = 0.66;

/**
 * Partner wordmarks, stacked in the first column of the design.
 *
 * `dropdepot` and `materiaux.archi` have no url yet, so they render as plain
 * images rather than as dead links.
 */

const partnerWordmarks: Logo[] = [{
  height: 22,
  href: 'https://archello.com/pt',
  name: 'Archello',
  src: assetUrlResolve('/static/images/partners/archello.svg'),
  width: 121
}, {
  height: 28,
  href: 'https://www.archiproducts.com/en/revigres',
  name: 'Archiproducts',
  src: assetUrlResolve('/static/images/partners/archiproducts.svg'),
  width: 160
}, {
  height: 28,
  href: 'https://www.bimobject.com/pt',
  name: 'BIMobject',
  src: assetUrlResolve('/static/images/partners/bimobject.svg'),
  width: 148
}, {
  height: 34,
  name: 'Dropdepot',
  src: assetUrlResolve('/static/images/partners/dropdepot.png'),
  width: 160
}, {
  height: 59,
  name: 'materiaux.archi',
  src: assetUrlResolve('/static/images/partners/materiaux-archi.svg'),
  width: 107
}];

/**
 * Award badges, laid out in a row beside the wordmarks.
 */

const partnerAwards: Logo[] = [{
  height: 111,
  href: 'https://www.chi-athenaeum.org/building-products/materials-2015/2015/12/21/chromatic-collection-2014/',
  name: 'Good Design Award',
  src: assetUrlResolve('/static/images/partners/good-design.svg'),
  width: 88
}, {
  height: 112,
  href: 'https://ifworlddesignguide.com/design-guide/profiles/companies?#/page/profile/6965-revigres',
  name: 'iF Design Award',
  src: assetUrlResolve('/static/images/partners/if-design-award.svg'),
  width: 54
}, {
  height: 108,
  href: 'https://www.red-dot.org',
  name: 'Red Dot Design Award',
  src: assetUrlResolve('/static/images/partners/reddot-design-award.svg'),
  width: 153
}];

/**
 * European funds logo, the first row of the `Apoios` block.
 */

const europeanFunds: Logo = {
  height: 32,
  name: 'Compete 2020, Portugal 2020, FEDER',
  src: assetUrlResolve('/static/images/supports/european-funds.svg'),
  width: 276
};

/**
 * Recovery and resilience plan logos, the second row of the `Apoios` block.
 */

const recoveryPlan: Logo[] = [{
  height: 25,
  name: 'PRR',
  src: assetUrlResolve('/static/images/supports/prr.svg'),
  width: 74
}, {
  height: 23,
  name: 'República Portuguesa',
  src: assetUrlResolve('/static/images/supports/republica-portuguesa.svg'),
  width: 84
}, {
  height: 24,
  name: 'Financiado pela União Europeia, NextGenerationEU',
  src: assetUrlResolve('/static/images/supports/next-generation-eu.svg'),
  width: 90
}];

/**
 * Certifications badge.
 *
 * Larger than in the design, at the client's request.
 */

const certifications: Logo = {
  height: 45,
  name: 'APCER, IQNet',
  src: assetUrlResolve('/static/images/certifications-2026.svg'),
  width: 134
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${units(5)};

  ${media.min('md')`
    align-items: stretch;
    flex-direction: row;
    gap: ${units(5)};
    justify-content: space-between;
  `}
`;

/**
 * `Block` styled component.
 *
 * In the design the three labels sit on one line at the top of the row and the
 * logos are pinned to the bottom, so each block stretches and pushes them
 * apart instead of keeping its label glued to its own logos.
 */

const Block = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${units(1)};

  ${media.min('md')`
    justify-content: space-between;
  `}
`;

/**
 * `CertificationsBlock` styled component.
 */

const CertificationsBlock = styled(Block)`
  ${media.min('md')`
    align-items: flex-end;
    text-align: right;
  `}
`;

/**
 * `Label` styled component.
 */

const Label = styled.p`
  color: ${color('white')};
  font-size: 14px;
  line-height: 1.5;
  margin: 0;
`;

/**
 * `Cluster` styled component.
 */

const Cluster = styled.div`
  align-items: flex-end;
  display: flex;
  flex-wrap: wrap;
  gap: ${units(3)};
`;

/**
 * `Wordmarks` styled component.
 */

const Wordmarks = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: ${units(2)};
`;

/**
 * `Awards` styled component.
 */

const Awards = styled.div`
  align-items: flex-end;
  display: flex;
  gap: ${units(2)};
`;

/**
 * `Rows` styled component.
 */

const Rows = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${units(3)};
`;

/**
 * `Row` styled component.
 */

const Row = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: ${units(2)};
`;

/**
 * `LogoImage` styled component.
 */

const LogoImage = styled.img<{ logoWidth: number }>`
  display: block;
  height: auto;
  width: ${({ logoWidth }) => Math.round(logoWidth * mobileScale)}px;

  ${media.min('ms')`
    width: ${prop('logoWidth')}px;
  `}
`;

/**
 * `LogoLink` styled component.
 */

const LogoLink = styled.a.attrs(navElementAttrs)`
  display: block;
  outline: none;
  transition: opacity ${theme('animations.fastTransition')};

  &:focus-visible,
  &:hover {
    opacity: 0.7;
  }

  &:focus-visible {
    outline: 1px solid ${color('white')};
    outline-offset: ${units(1)};
  }
`;

/**
 * Render a logo, wrapped in a link when it has a destination.
 */

function renderLogo({ height, href, name, src, width }: Logo): ReactElement {
  const image = (
    <LogoImage
      alt={name}
      height={height}
      loading={'lazy'}
      logoWidth={width}
      src={src}
      width={width}
    />
  );

  if (!href) {
    return image;
  }

  return (
    <LogoLink
      aria-label={name}
      href={href}
    >
      {image}
    </LogoLink>
  );
}

/**
 * `Logos` component.
 *
 * The `Parceiros`, `Apoios` and `Certificações` row of the footer.
 */

const Logos = (): ReactElement => {
  const { translate } = useTranslate();

  return (
    <Wrapper>
      <Block>
        <Label>
          {translate('footer:partners')}
        </Label>

        <Cluster>
          <Wordmarks>
            {map(partnerWordmarks, logo => (
              <React.Fragment key={logo.name}>
                {renderLogo(logo)}
              </React.Fragment>
            ))}
          </Wordmarks>

          <Awards>
            {map(partnerAwards, logo => (
              <React.Fragment key={logo.name}>
                {renderLogo(logo)}
              </React.Fragment>
            ))}
          </Awards>
        </Cluster>
      </Block>

      <Block>
        <Label>
          {translate('footer:supports')}
        </Label>

        <Rows>
          {renderLogo(europeanFunds)}

          <Row>
            {map(recoveryPlan, logo => (
              <React.Fragment key={logo.name}>
                {renderLogo(logo)}
              </React.Fragment>
            ))}
          </Row>
        </Rows>
      </Block>

      <CertificationsBlock>
        <Label>
          {translate('footer:certifications')}
        </Label>

        {renderLogo(certifications)}
      </CertificationsBlock>
    </Wrapper>
  );
};

/**
 * Export `Logos` component.
 */

export default Logos;
